import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  Input,
  InputGroup,
  InputLeftAddon,
  InputRightAddon,
  Radio,
  Td,
  Tr,
  DatePicker,
} from '@frontend/components';

import { LEAD_TIME_UNITS, type LeadTimeUnit } from 'constants/all';

import { useAppSelector } from 'store/hooks';
import { getOrder } from 'store/orders/selectors';
import { ActionPayload } from 'store/types';

import { toMoment } from 'utils/dateUtils';
import { formatPrice } from 'utils/formatters';

import { DRAWER_MODES } from '../constants';
import { adjustments } from '../data/actions';
import { getAdjustment, getDrawerMode } from '../data/reducer';
import {
  getLeadTimeFromShipsOnDate,
  getShipsOnDateFromLeadTime,
} from '../utils';

const formatInteger = (intString: string | null): number | '' => {
  if (intString !== null && intString !== '') {
    const strippedString = intString.replace(/[^0-9]/g, '');
    return strippedString !== '' ? parseInt(strippedString) : '';
  } else {
    return '';
  }
};

type AdjustmentProps = {
  quoteItemId: number;
  leadTimeDisplayUnit: LeadTimeUnit;
  unitPricePrecision: number;
  inputRef: React.RefObject<HTMLInputElement>;
};

export const Adjustment = ({
  quoteItemId,
  leadTimeDisplayUnit,
  unitPricePrecision,
  inputRef,
}: AdjustmentProps) => {
  const dispatch = useDispatch();
  const deleteAdjustment = useCallback(
    (data: ActionPayload<typeof adjustments.delete>) => {
      dispatch(adjustments.delete(data));
    },
    [dispatch],
  );
  const updateAdjustment = useCallback(
    (data: ActionPayload<typeof adjustments.update>) => {
      dispatch(adjustments.update(data));
    },
    [dispatch],
  );
  const adjustment = useAppSelector((state) =>
    getAdjustment(state, quoteItemId),
  );
  const drawerMode = useAppSelector(getDrawerMode);
  const order = useAppSelector(getOrder);
  const orderCreatedDate =
    drawerMode === DRAWER_MODES.EDIT && order ? order.created : undefined;

  const [localQuantity, setLocalQuantity] = useState<'' | number>('');
  const [localLeadTime, setLocalLeadTime] = useState<'' | number>('');
  const [localShipsOn, setLocalShipsOn] = useState<'' | string>('');
  const [localUnitPrice, setLocalUnitPrice] = useState('');

  useEffect(() => {
    const { quantity, leadTime, unitPrice, shipsOn } = adjustment;
    setLocalQuantity(quantity);
    setLocalLeadTime(leadTime ?? '');
    setLocalShipsOn(shipsOn || '');
    setLocalUnitPrice(unitPrice || '');
  }, [adjustment]);

  return (
    <Tr isSelected>
      <Td
        textAlign="right"
        cursor="pointer"
        onClick={() => {
          deleteAdjustment({
            adjustmentId: adjustment.id,
            quoteItemId: quoteItemId,
          });
        }}
      >
        <Radio isChecked />
      </Td>
      <Td>
        <Input
          size="sm"
          ref={inputRef}
          textAlign="right"
          value={localQuantity}
          onChange={(e) => {
            setLocalQuantity(formatInteger(e.target.value));
          }}
          onBlur={() => {
            updateAdjustment({
              quoteItemId: quoteItemId,
              data: { quantity: localQuantity },
            });
          }}
        />
      </Td>
      <Td>
        <InputGroup size="sm">
          <Input
            textAlign="right"
            value={localLeadTime}
            onChange={(e) => {
              setLocalLeadTime(formatInteger(e.target.value));
              setLocalShipsOn('');
            }}
            onBlur={() => {
              updateAdjustment({
                quoteItemId: quoteItemId,
                data: { leadTime: localLeadTime, shipsOn: localShipsOn },
              });
            }}
          />
          <InputRightAddon>
            {LEAD_TIME_UNITS[leadTimeDisplayUnit].unitName.toLowerCase()}s
          </InputRightAddon>
        </InputGroup>
      </Td>
      <Td
        paddingX={0.75}
        sx={{
          '.ant-picker.ant-picker-small': {
            paddingX: 0.75,
            height: '100%',
          },
          '.ant-picker-input > input': {
            fontSize: 'sm',
          },
        }}
      >
        <DatePicker
          size="small"
          allowClear={false}
          disabledDate={(current) => current.isBefore(moment(), 'day')}
          value={
            localShipsOn
              ? toMoment(localShipsOn)
              : localLeadTime !== '' && localLeadTime != null
              ? toMoment(
                  getShipsOnDateFromLeadTime({
                    fromDate: orderCreatedDate
                      ? new Date(orderCreatedDate)
                      : new Date(),
                    leadTime: localLeadTime,
                    leadTimeDisplayUnit: leadTimeDisplayUnit,
                  }).toISOString(),
                )
              : undefined
          }
          onChange={(newMoment) => {
            if (newMoment) {
              const leadTime = getLeadTimeFromShipsOnDate({
                fromDate: orderCreatedDate
                  ? new Date(orderCreatedDate)
                  : new Date(),
                shipsOnDate: newMoment.toDate(),
                leadTimeDisplayUnit: leadTimeDisplayUnit,
              });
              const shipsOn = newMoment.format('YYYY-MM-DD');
              setLocalLeadTime(leadTime);
              setLocalShipsOn(shipsOn);
              dispatch(
                adjustments.update({
                  quoteItemId: quoteItemId,
                  data: {
                    leadTime,
                    shipsOn,
                  },
                }),
              );
            } else {
              setLocalLeadTime('');
              setLocalShipsOn('');
              dispatch(
                adjustments.update({
                  quoteItemId: quoteItemId,
                  data: {
                    leadTime: '',
                    shipsOn: '',
                  },
                }),
              );
            }
          }}
        />
      </Td>
      <Td>
        <InputGroup size="sm">
          <InputLeftAddon>$</InputLeftAddon>
          <Input
            textAlign="right"
            value={
              !isNaN(Number(localUnitPrice)) && localUnitPrice.trim() !== ''
                ? `${localUnitPrice}`
                : ''
            }
            onChange={(e) => {
              if (
                !e.target.value.includes('.') ||
                e.target.value.split('.')[1].length <= unitPricePrecision
              ) {
                setLocalUnitPrice(e.target.value.replace(/[^0-9.]/g, ''));
              }
            }}
            onBlur={() => {
              const localValue =
                parseFloat(localUnitPrice).toFixed(unitPricePrecision);
              const nextValue = isNaN(Number(localValue)) ? '' : localValue;

              updateAdjustment({
                quoteItemId: quoteItemId,
                data: {
                  unitPrice: nextValue,
                },
              });
              setLocalUnitPrice(nextValue);
            }}
          />
        </InputGroup>
      </Td>
      <Td textAlign="right" fontFamily="data">
        {adjustment.totalPrice ? formatPrice(adjustment.totalPrice) : '--'}
      </Td>
    </Tr>
  );
};
